import React, { forwardRef, useState, useMemo, useEffect } from "react";
import { getThemeURL } from "../../../../Services/EndPoints";
import { getData } from "../../../../Services/Services";
import { useNavigate } from "react-router-dom";

const GrievanceTicketsList = forwardRef((props, ref) => {
  const { tickets = [] } = props;

  // Add state for API data
  const [apiTickets, setApiTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: 'createdDate',
    direction: 'desc'
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  // const [username, setusername] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // View mode state
  // eslint-disable-next-line no-unused-vars
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'list'

  const Navigate = useNavigate();

  // Use either API tickets or passed tickets prop
  const displayTickets = apiTickets.length > 0 ? apiTickets : tickets;

  // Helper function to convert numeric status to text - FIXED
  const getStatusText = (statusCode) => {
    const statusMap = {
      '837': 'Open',
      '838': 'In Progress',
      '841': 'Closed',
      'Open': 'Open',
      'In Progress': 'In Progress',
      'Closed': 'Closed'
    };
    return statusMap[statusCode?.toString()] || 'Open';
  };

  // Get unique values for filters
  const statusOptions = useMemo(() => {
    const statuses = ['All', 'Open', 'In Progress', 'Closed'];
    return statuses;
  }, []);

  const priorityOptions = useMemo(() => {
    const priorities = ['All', ...new Set(displayTickets
      .map(ticket => ticket.priority)
      .filter(Boolean)
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    )];
    return priorities;
  }, [displayTickets]);

  const categoryOptions = useMemo(() => {
    const staticCategories = [
      { value: 'All', label: 'All Categories' },
      { value: 'maintenance', label: 'Maintenance & Repairs' },
      { value: 'rent', label: 'Rent / Payment' },
      { value: 'amenities', label: 'Amenities Issues' },
      { value: 'noise', label: 'Noise Complaints' },
      { value: 'safety', label: 'Safety / Security issues' },
      { value: 'other', label: 'Other' }
    ];
    return staticCategories;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTickets]);

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = [...displayTickets];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket =>
        (ticket.subject && ticket.subject.toLowerCase().includes(term)) ||
        (ticket.description && ticket.description.toLowerCase().includes(term)) ||
        (ticket.id && ticket.id.toString().toLowerCase().includes(term)) ||
        (ticket.category && ticket.category.toLowerCase().includes(term)) ||
        (ticket.customerName && ticket.customerName.toLowerCase().includes(term))
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(ticket => {
        const statusText = getStatusText(ticket.status);
        return statusText === statusFilter;
      });
    }

    // Priority filter
    if (priorityFilter !== 'All') {
      filtered = filtered.filter(ticket => {
        const ticketPriority = ticket.priority ? 
          ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) : '';
        return ticketPriority === priorityFilter;
      });
    }

    // Category filter
    if (categoryFilter !== 'All') {
      filtered = filtered.filter(ticket => 
        ticket.category && ticket.category === categoryFilter
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayTickets, searchTerm, statusFilter, priorityFilter, categoryFilter, sortConfig]);

  // Pagination logic
  const paginationData = useMemo(() => {
    const totalItems = filteredAndSortedTickets.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    const currentItems = filteredAndSortedTickets.slice(startIndex, endIndex);

    return {
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      currentItems
    };
  }, [filteredAndSortedTickets, currentPage, itemsPerPage]);

  // Use paginated items for BOTH table and list views
  const displayedTickets = paginationData.currentItems;

  // Helper function to format date in DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    return apiData.map(item => {
      const statusCode = item.status?.toString();
      const statusText = getStatusText(statusCode);
      
      return {
        id: item.grievanceSeqId?.toString() || 'N/A',
        subject: item.subject || 'No Subject',
        description: item.description || '',
        category: item.categoryName || 'Uncategorized',
        priority: item.priority ? 
          item.priority.charAt(0).toUpperCase() + item.priority.slice(1).toLowerCase() 
          : 'Low',
        status: statusText,
        createdDate: item.created_on || new Date().toISOString(),
        updatedDate: item.updated_on || item.created_on || new Date().toISOString(),
        customerName: item.customerName || 'Unknown Customer',
        propertyAddress: item.propertyAddress || '',
        apartmentNumber: item.aprtmentNumber || item.apartmentNumber || '',
        phoneNumber: item.phoneNumber || '',
        alternatePhoneNumber: item.alternatePhoneNumber || '',
        inprogressRemarks: item.inprogressRemarks || '',
        relPropertyName: item.relPropertyName || '',
        closedRemarks: item.closedRemarks || '',
        email: item.email || '',
        mediaList: item.mediaList || [],
        originalData: item
      };
    });
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setPriorityFilter('All');
    setCategoryFilter('All');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Status badge styling
  const getStatusStyle = (statusCode) => {
    const statusText = getStatusText(statusCode);
    const styles = {
      Open: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fbbf24'
      },
      'In Progress': {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        border: '1px solid #60a5fa'
      },
      Closed: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        border: '1px solid #34d399'
      }
    };
    return styles[statusText] || styles['Open'];
  };

  // Priority styling
  const getPriorityStyle = (priority) => {
    const priorityCapitalized = priority ? 
      priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase() 
      : 'Low';
    
    const styles = {
      High: {
        color: '#dc2626',
        fontWeight: '600',
        backgroundColor: '#fee2e2',
        padding: '2px 8px',
        borderRadius: '4px',
        display: 'inline-block'
      },
      Medium: {
        color: '#ea580c',
        fontWeight: '500',
        backgroundColor: '#ffedd5',
        padding: '2px 8px',
        borderRadius: '4px',
        display: 'inline-block'
      },
      Low: {
        color: '#16a34a',
        fontWeight: '500',
        backgroundColor: '#dcfce7',
        padding: '2px 8px',
        borderRadius: '4px',
        display: 'inline-block'
      }
    };
    return styles[priorityCapitalized] || styles['Low'];
  };

  const handleView = (ticket) => {
    Navigate("/grievance-rental/grievance-rental-view-tickets", {
      state: { ticketData: ticket }
    });
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = displayTickets.length;
    const filtered = filteredAndSortedTickets.length;
    const open = displayTickets.filter(t => getStatusText(t.status) === 'Open').length;
    const inProgress = displayTickets.filter(t => getStatusText(t.status) === 'In Progress').length;
    const closed = displayTickets.filter(t => getStatusText(t.status) === 'Closed').length;
   
    return { total, filtered, open, inProgress, closed };
  }, [displayTickets, filteredAndSortedTickets]);

  // Dynamic endpoint generator
  const getGrievanceEndpoint = (user) => {
    if (!user) return '';
    const userType = user.role_name === 'SEOC' ? 'SA' : 'U';
    return `/grievance/getall/${user.id}/${userType}`;
  };

  // Fetch user data and grievance data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const usr_prof = await getData(getThemeURL);
        if (usr_prof && usr_prof[0]) {
          const userData = usr_prof[0];
          // setusername(userData);
          
          const endpoint = getGrievanceEndpoint(userData);
          if (endpoint) {
            const response = await getData(endpoint);
            const transformedData = transformApiData(response);
            setApiTickets(transformedData);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, priorityFilter, categoryFilter]);

  // If loading API data
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <div style={styles.loadingSpinner}>⏳</div>
          <p>Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} style={styles.container}>
      {/* Header with stats and view toggle */}
      <div style={styles.header}>
        <div>
          <div style={styles.stats}>
            <span style={styles.statItem}>
              Total: <strong>{stats.total}</strong>
            </span>
            <span style={styles.statItem}>
              Open: <strong style={{color: '#92400e'}}>{stats.open}</strong>
            </span>
            <span style={styles.statItem}>
              In Progress: <strong style={{color: '#1e40af'}}>{stats.inProgress}</strong>
            </span>
            <span style={styles.statItem}>
              Closed: <strong style={{color: '#065f46'}}>{stats.closed}</strong>
            </span>
          </div>
        </div>
       
        {/* View Toggle */}
        {/* <div style={styles.viewToggle}>
          <button
            style={{
              ...styles.viewToggleButton,
              ...(viewMode === 'table' ? styles.viewToggleActive : {})
            }}
            onClick={() => setViewMode('table')}
            title="Table View"
          >
            <span style={styles.viewIcon}>📋</span>
            Table
          </button>
          <button
            style={{
              ...styles.viewToggleButton,
              ...(viewMode === 'list' ? styles.viewToggleActive : {})
            }}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <span style={styles.viewIcon}>📄</span>
            List
          </button>
        </div> */}
      </div>

      {/* All Filters in One Line */}
      <div style={styles.filtersRow}>
        <div style={styles.searchBox}>
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
       
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.filterDropdown}
        >
          {statusOptions.map(status => (
            <option key={status} value={status}>
              {status === 'All' ? 'All Status' : status}
            </option>
          ))}
        </select>
       
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          style={styles.filterDropdown}
        >
          {priorityOptions.map(priority => {
            const displayPriority = priority === 'All' ? 'All Priority' : priority;
            return (
              <option key={priority} value={priority}>
                {displayPriority}
              </option>
            );
          })}
        </select>
       
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={styles.filterDropdown}
        >
          {categoryOptions.map(category => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>
       
        <button
          style={styles.resetButton}
          onClick={resetFilters}
          disabled={!searchTerm && statusFilter === 'All' && priorityFilter === 'All' && categoryFilter === 'All'}
          title="Reset all filters"
        >
          Reset
        </button>
      </div>

      {filteredAndSortedTickets.length > 0 ? (
        viewMode === 'table' ? (
          // Table View
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>
                    <div style={styles.thContent}>
                      Ticket ID
                      <span
                        style={styles.sortIcon}
                        onClick={() => handleSort('id')}
                      >
                        ↕
                      </span>
                    </div>
                  </th>
                  <th style={styles.th}>
                    <div style={styles.thContent}>
                      Title
                      <span
                        style={styles.sortIcon}
                        onClick={() => handleSort('subject')}
                      >
                        ↕
                      </span>
                    </div>
                  </th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>
                    <div style={styles.thContent}>
                      Created Date
                      <span
                        style={styles.sortIcon}
                        onClick={() => handleSort('createdDate')}
                      >
                        ↕
                      </span>
                    </div>
                  </th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Use displayedTickets instead of filteredAndSortedTickets */}
                {displayedTickets.map((ticket, index) => (
                  <tr key={ticket.id || index} style={styles.tableRow}>
                    <td style={styles.td}>
                      <span style={styles.ticketId}>#{ticket.id}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.subjectCell}>
                        <div style={styles.subject}>{ticket.subject}</div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.category}>
                        {ticket.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={getPriorityStyle(ticket.priority)}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{...styles.statusBadge, ...getStatusStyle(ticket.status)}}>
                        {getStatusText(ticket.status)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.dateCell}>
                        <div>{formatDate(ticket.createdDate)}</div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.actionButton}
                          onClick={() => handleView(ticket)}
                          title="View Details"
                        >
                          <i className="fa fa-eye" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // List/Card View
          <div style={styles.listContainer}>
            {/* Use displayedTickets for list view too */}
            {displayedTickets.map((ticket, index) => (
              <div key={ticket.id || index} style={styles.listCard}>
                <div style={styles.listCardHeader}>
                  <div style={styles.listCardTitle}>
                    <span style={styles.listTicketId}>#{ticket.id}</span>
                    <h4 style={styles.listSubject}>{ticket.subject}</h4>
                  </div>
                  <div style={styles.listCardActions}>
                    <button
                      style={styles.listActionButton}
                      onClick={() => handleView(ticket)}
                      title="View Details"
                    >
                      👁️
                    </button>
                  </div>
                </div>
               
                <div style={styles.listCardBody}>
                  {ticket.description && (
                    <p style={styles.listDescription}>
                      {ticket.description}
                    </p>
                  )}
                 
                  <div style={styles.listCardMeta}>
                    <div style={styles.listMetaRow}>
                      <div style={styles.listMetaItem}>
                        <span style={styles.metaLabel}>Customer:</span>
                        <span style={styles.customerName}>{ticket.customerName}</span>
                      </div>
                      <div style={styles.listMetaItem}>
                        <span style={styles.metaLabel}>Category:</span>
                        <span style={styles.listCategory}>
                          {ticket.category || 'Uncategorized'}
                        </span>
                      </div>
                      <div style={styles.listMetaItem}>
                        <span style={styles.metaLabel}>Priority:</span>
                        <span style={getPriorityStyle(ticket.priority)}>
                          {ticket.priority}
                        </span>
                      </div>
                      <div style={styles.listMetaItem}>
                        <span style={styles.metaLabel}>Status:</span>
                        <span style={{...styles.listStatusBadge, ...getStatusStyle(ticket.status)}}>
                          {getStatusText(ticket.status)}
                        </span>
                      </div>
                    </div>
                   
                    <div style={styles.listMetaRow}>
                      <div style={styles.listMetaItem}>
                        <span style={styles.metaLabel}>Created:</span>
                        <span>{formatDate(ticket.createdDate)}</span>
                      </div>
                      {ticket.updatedDate && ticket.updatedDate !== ticket.createdDate && (
                        <div style={styles.listMetaItem}>
                          <span style={styles.metaLabel}>Updated:</span>
                          <span>{formatDate(ticket.updatedDate)}</span>
                        </div>
                      )}
                      {ticket.apartmentNumber && (
                        <div style={styles.listMetaItem}>
                          <span style={styles.metaLabel}>Apartment:</span>
                          <span>{ticket.apartmentNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <p style={styles.emptyText}>
            {displayTickets.length === 0
              ? "No tickets available"
              : "No tickets match your search criteria"}
          </p>
        </div>
      )}

      {/* Pagination Component - Common for both views */}
      {filteredAndSortedTickets.length > 0 && (
        <div style={styles.paginationContainer}>
          <div style={styles.paginationInfo}>
            Showing {paginationData.startIndex + 1} to {paginationData.endIndex} of {paginationData.totalItems} entries
            {filteredAndSortedTickets.length !== displayTickets.length && (
              <span style={{ marginLeft: '10px', color: '#64748b', fontSize: '12px' }}>
                (Filtered from {displayTickets.length} total entries)
              </span>
            )}
          </div>

          <div style={styles.paginationControls}>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === 1 ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First Page"
            >
              «
            </button>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === 1 ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              title="Previous Page"
            >
              ‹
            </button>

            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
              let pageNum;
              if (paginationData.totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= paginationData.totalPages - 2) {
                pageNum = paginationData.totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  style={{
                    ...styles.paginationButton,
                    ...(currentPage === pageNum ? styles.paginationButtonActive : {})
                  }}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === paginationData.totalPages ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages))}
              disabled={currentPage === paginationData.totalPages}
              title="Next Page"
            >
              ›
            </button>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === paginationData.totalPages ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setCurrentPage(paginationData.totalPages)}
              disabled={currentPage === paginationData.totalPages}
              title="Last Page"
            >
              »
            </button>
          </div>

          <div style={styles.itemsPerPageSelector}>
            <span style={styles.itemsPerPageLabel}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              style={styles.itemsPerPageSelect}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span style={styles.itemsPerPageLabel}>entries</span>
          </div>
        </div>
      )}
    </div>
  );
});

// CSS styles
const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
    gap: '20px'
  },
  stats: {
    display: 'flex',
    gap: '10px',
    fontSize: '14px',
    color: '#64748b',
    flexWrap: 'wrap'
  },
  statItem: {
    padding: '6px 12px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    gap: '5px',
    alignItems: 'center'
  },
  // View Toggle
  viewToggle: {
    display: 'flex',
    gap: '5px',
    backgroundColor: '#f1f5f9',
    borderRadius: '8px',
    padding: '4px'
  },
  viewToggleButton: {
    padding: '8px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  },
  viewToggleActive: {
    backgroundColor: 'white',
    color: '#3b82f6',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  viewIcon: {
    fontSize: '16px'
  },
  // Filters in one row
  filtersRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  searchBox: {
    flex: 2,
    minWidth: '200px'
  },
  searchInput: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    ':focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    }
  },
  filterDropdown: {
    flex: 1,
    minWidth: '150px',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    fontSize: '14px',
    color: '#475569',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':focus': {
      outline: 'none',
      borderColor: '#3b82f6',
    }
  },
  resetButton: {
    padding: '10px 20px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    ':hover:not(:disabled)': {
      backgroundColor: '#e2e8f0',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    }
  },
  // Table View Styles
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    marginBottom: '20px' // Add margin to separate from pagination
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    minWidth: '800px'
    
  },
  tableHeader: {
    backgroundColor: '#1e293b', // Changed to dark color
    borderBottom: '2px solid #e2e8f0'
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#ffffff', // White text
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    userSelect: 'none'
  },
  thContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sortIcon: {
    fontSize: '12px',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '2px',
    borderRadius: '3px',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#334155',
      color: '#ffffff',
    }
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#f8fafc'
    }
  },
  td: {
    padding: '12px 16px',
    verticalAlign: 'middle',
    textAlign: 'left'
  },
  ticketId: {
    fontFamily: 'monospace',
    fontWeight: '600',
    color: '#0f172a',
    fontSize: '13px',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block'
  },
  subjectCell: {
    maxWidth: '250px'
  },
  subject: {
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '4px',
    fontSize: '14px'
  },
  description: {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: '1.4'
  },
  category: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#f1f5f9',
    borderRadius: '20px',
    fontSize: '13px',
    color: '#475569',
    border: '1px solid #e2e8f0'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
    minWidth: '100px'
  },
  dateCell: {
    fontSize: '13px'
  },
  updatedDate: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  actionButton: {
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#475569',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#cbd5e1'
    }
  },
  // List View Styles
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  listCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    padding: '20px',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: '#cbd5e1',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    }
  },
  listCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  listCardTitle: {
    flex: 1,
    minWidth: '200px'
  },
  listTicketId: {
    fontFamily: 'monospace',
    fontWeight: '600',
    color: '#64748b',
    fontSize: '12px',
    marginBottom: '5px',
    display: 'inline-block'
  },
  listSubject: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    lineHeight: '1.4'
  },
  listCardActions: {
    display: 'flex',
    gap: '8px'
  },
  listActionButton: {
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#475569',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#cbd5e1'
    }
  },
  listCardBody: {
    marginTop: '10px'
  },
  listDescription: {
    fontSize: '14px',
    color: '#475569',
    lineHeight: '1.6',
    margin: '0 0 15px 0'
  },
  listCardMeta: {
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    padding: '15px',
    border: '1px solid #e2e8f0'
  },
  listMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '10px',
    ':last-child': {
      marginBottom: 0
    }
  },
  listMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  },
  metaLabel: {
    color: '#64748b',
    fontWeight: '500'
  },
  listCategory: {
    padding: '4px 10px',
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    fontSize: '12px',
    color: '#475569',
    border: '1px solid #e2e8f0'
  },
  listStatusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
    minWidth: '80px'
  },
  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#94a3b8',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px dashed #e2e8f0',
    marginTop: '20px'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: '0.5'
  },
  emptyText: {
    fontSize: '14px',
    margin: '0 0 20px 0',
    color: '#64748b'
  },
  clearButton: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    marginRight: '12px',
    ':hover': {
      backgroundColor: '#e2e8f0',
    }
  },
  createButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#2563eb'
    }
  },
  // Loading State
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#94a3b8',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px dashed #e2e8f0',
    marginTop: '20px'
  },
  loadingSpinner: {
    fontSize: '48px',
    marginBottom: '16px',
    animation: 'spin 1s linear infinite'
  },
  customerName: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px',
    fontStyle: 'italic'
  },
  
  
  // Animation for loading spinner
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },

  paginationContainer: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px 20px',
  backgroundColor: '#f8fafc',
  borderTop: '1px solid #e2e8f0',
  borderBottomLeftRadius: '6px',
  borderBottomRightRadius: '6px',
  flexWrap: 'wrap',
  gap: '15px'
},
paginationInfo: {
  fontSize: '14px',
  color: '#64748b',
  minWidth: '200px'
},
paginationControls: {
  display: 'flex',
  gap: '5px',
  flexWrap: 'wrap',
  justifyContent: 'center',
  flex: 1
},
paginationButton: {
  padding: '8px 12px',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#475569',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minWidth: '40px',
  textAlign: 'center',
  ':hover:not(:disabled)': {
    backgroundColor: '#f1f5f9',
    borderColor: '#cbd5e1'
  }
},
paginationButtonActive: {
  backgroundColor: '#3b82f6',
  color: '#ffffff',
  borderColor: '#3b82f6',
  ':hover': {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb'
  }
},
paginationButtonDisabled: {
  opacity: 0.5,
  cursor: 'not-allowed',
  ':hover': {
    backgroundColor: '#ffffff',
    borderColor: '#e2e8f0'
  }
},
itemsPerPageSelector: {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: '150px',
  justifyContent: 'flex-end'
},
itemsPerPageLabel: {
  fontSize: '14px',
  color: '#64748b'
},
itemsPerPageSelect: {
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#ffffff',
  fontSize: '14px',
  color: '#475569',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  ':focus': {
    outline: 'none',
    borderColor: '#3b82f6',
  }
}
};

// If you need to keep default props for testing
GrievanceTicketsList.defaultProps = {
  tickets: [],
  onView: (ticket) => console.log('View ticket:', ticket.id),
  onEdit: (ticket) => console.log('Edit ticket:', ticket.id || 'new'),
  onDelete: (ticket) => console.log('Delete ticket:', ticket.id)
};

export default GrievanceTicketsList;